import { HOURS_BY_DAY } from "./restaurantFacts";
import type { MutationCtx, QueryCtx } from "../_generated/server";

export const DEFAULT_OWNER_GROUP_ID = "steven-kennedy-restaurants";
export const DEFAULT_RESTAURANT_ID = "tuscan-cove";
export const DEFAULT_LOCATION_ID = "tuscan-cove-southern-highlands";
export const ROMA_RESTAURANT_ID = "roma-kitchen";
export const ROMA_LOCATION_ID = "roma-kitchen-southern-highlands";
export const DEMO_PROFESSIONAL_SERVICES_OWNER_GROUP_ID = "demo-professional-services";
export const ACME_BARBER_BUSINESS_ID = "acme-barber";
export const ACME_BARBER_LOCATION_ID = "acme-barber-main";

export type BusinessVerticalId =
  | "restaurant"
  | "barber_shop"
  | "generic_local_business";

export type PlatformPackageCapabilities = {
  core: boolean;
  guestManager: boolean;
  aiVoice: boolean;
};

export type AdminLocationSummary = {
  /** Existing tenant branch with ops/voice scope but no separate public site. */
  operationalOnly?: boolean;
  id: string;
  businessId: string;
  tenantId: string;
  restaurantId: string;
  verticalId: BusinessVerticalId;
  ownerGroupId: string;
  name: string;
  shortName: string;
  publicPath: string;
  publicUrl?: string;
  /** Server-side package snapshot. Missing means core-only. */
  capabilities?: PlatformPackageCapabilities;
  /** IANA zone when the platform has an authoritative value. */
  timeZone?: string;
};

export type AdminLocationContext = {
  ownerGroupId: string;
  businessId: string;
  tenantId: string;
  restaurantId: string;
  verticalId: BusinessVerticalId;
  locationId: string;
  activeLocation: AdminLocationSummary;
  availableLocations: AdminLocationSummary[];
};

type AdminLocationUser = {
  ownerGroupId?: string;
  defaultBusinessId?: string;
  defaultTenantId?: string;
  defaultRestaurantId?: string;
  defaultLocationId?: string;
  accessibleLocationIds?: string[];
};

export type BusinessScopedRecord = {
  businessId?: string;
  tenantId?: string;
  restaurantId?: string;
  locationId?: string;
};

export type ScopedRestaurantRecord = BusinessScopedRecord;

export type ScopedInsertFields = {
  restaurantId: string;
  locationId: string;
};

export type BusinessScopedInsertFields = {
  businessId: string;
  tenantId: string;
  restaurantId: string;
  locationId: string;
};

export type LocationContextLike = Pick<
  AdminLocationContext,
  "restaurantId" | "locationId"
> &
  Partial<Pick<AdminLocationContext, "businessId" | "tenantId">>;

type ReservationHours = {
  open: string;
  close: string;
  /**
   * Minutes before close of the last bookable slot. Absent = the dining
   * duration (90), so no booking runs past close.
   */
  lastSeatingBufferMinutes?: number;
} | null;

// Roma takes its last seating 30 minutes before close: 8:30 PM Mon-Sat and
// 7:30 PM Sunday (Sunday closes at 8 PM).
const ROMA_LAST_SEATING_BUFFER_MINUTES = 30;

const ROMA_RESERVATION_HOURS_BY_DAY: Record<number, ReservationHours> = {
  0: { open: "16:00", close: "20:00", lastSeatingBufferMinutes: ROMA_LAST_SEATING_BUFFER_MINUTES },
  1: { open: "16:00", close: "21:00", lastSeatingBufferMinutes: ROMA_LAST_SEATING_BUFFER_MINUTES },
  2: { open: "16:00", close: "21:00", lastSeatingBufferMinutes: ROMA_LAST_SEATING_BUFFER_MINUTES },
  3: { open: "16:00", close: "21:00", lastSeatingBufferMinutes: ROMA_LAST_SEATING_BUFFER_MINUTES },
  4: { open: "16:00", close: "21:00", lastSeatingBufferMinutes: ROMA_LAST_SEATING_BUFFER_MINUTES },
  5: { open: "16:00", close: "21:00", lastSeatingBufferMinutes: ROMA_LAST_SEATING_BUFFER_MINUTES },
  6: { open: "16:00", close: "21:00", lastSeatingBufferMinutes: ROMA_LAST_SEATING_BUFFER_MINUTES },
};

/** Minutes-since-midnight of the last bookable slot for one day's hours. */
export function reservationLastSlotMinutes(
  hours: { close: string; lastSeatingBufferMinutes?: number },
  defaultBufferMinutes: number,
): number {
  const [h, m] = hours.close.split(":").map(Number);
  return h * 60 + m - (hours.lastSeatingBufferMinutes ?? defaultBufferMinutes);
}

export const PLATFORM_ADMIN_LOCATIONS: AdminLocationSummary[] = [

  {
    id: DEFAULT_LOCATION_ID,
    businessId: DEFAULT_RESTAURANT_ID,
    tenantId: DEFAULT_RESTAURANT_ID,
    restaurantId: DEFAULT_RESTAURANT_ID,
    verticalId: "restaurant",
    ownerGroupId: DEFAULT_OWNER_GROUP_ID,
    name: "Tuscan Cove Bar & Patio",
    shortName: "Tuscan Cove",
    publicPath: "/locations/tuscan-cove-southern-highlands",
    publicUrl: "https://tuscancove.com/locations/tuscan-cove-southern-highlands",
    capabilities: { core: true, guestManager: true, aiVoice: true },
    timeZone: "America/Los_Angeles",
  },
  {
    id: ROMA_LOCATION_ID,
    businessId: ROMA_RESTAURANT_ID,
    tenantId: ROMA_RESTAURANT_ID,
    restaurantId: ROMA_RESTAURANT_ID,
    verticalId: "restaurant",
    ownerGroupId: DEFAULT_OWNER_GROUP_ID,
    name: "Roma Kitchen",
    shortName: "Roma Kitchen",
    publicPath: "/locations/roma-kitchen-southern-highlands",
    publicUrl: "https://chefsromakitchen.com/",
    capabilities: { core: true, guestManager: true, aiVoice: false },
    timeZone: "America/Los_Angeles",
  },
  {
    id: ACME_BARBER_LOCATION_ID,
    businessId: ACME_BARBER_BUSINESS_ID,
    tenantId: ACME_BARBER_BUSINESS_ID,
    restaurantId: ACME_BARBER_BUSINESS_ID,
    verticalId: "barber_shop",
    ownerGroupId: DEMO_PROFESSIONAL_SERVICES_OWNER_GROUP_ID,
    name: "Acme Barber Shop",
    shortName: "Acme Barber",
    publicPath: "/locations/acme-barber-main",
    publicUrl: "https://acme-barber.example/locations/acme-barber-main",
    capabilities: { core: true, guestManager: false, aiVoice: false },
  },
  {
    id: "roma-kitchen-henderson",
    businessId: "roma-kitchen",
    tenantId: "roma-kitchen",
    restaurantId: "roma-kitchen",
    verticalId: "restaurant",
    ownerGroupId: "steven-kennedy-restaurants",
    name: "Roma Kitchen Henderson",
    shortName: "Roma Henderson",
    operationalOnly: true,
    publicPath: "/best/italian-restaurant-henderson-nv",
    publicUrl: "https://chefsromakitchen.com/best/italian-restaurant-henderson-nv",
  },
];

function getKnownOwnerGroupId(
  ownerGroupId: string | undefined,
  locations: AdminLocationSummary[] = PLATFORM_ADMIN_LOCATIONS,
) {
  return locations.some(
    (location) => location.ownerGroupId === ownerGroupId,
  )
    ? ownerGroupId
    : DEFAULT_OWNER_GROUP_ID;
}

function getDefaultLocation(
  locations: AdminLocationSummary[] = PLATFORM_ADMIN_LOCATIONS,
) {
  return (
    locations.find((location) => location.id === DEFAULT_LOCATION_ID) ??
    locations[0] ??
    PLATFORM_ADMIN_LOCATIONS.find((location) => location.id === DEFAULT_LOCATION_ID) ??
    PLATFORM_ADMIN_LOCATIONS[0]
  );
}

function getKnownPublicLocation(
  locationId: string | undefined,
  locations: AdminLocationSummary[] = PLATFORM_ADMIN_LOCATIONS,
) {
  if (locationId === undefined) return getDefaultLocation(locations);

  const location = locations.find(
    (candidate) => candidate.id === locationId,
  );
  if (!location) {
    throw new Error("Unsupported business location");
  }
  return location;
}

function locationMatchesTenantId(
  location: Pick<AdminLocationSummary, "restaurantId"> &
    Partial<Pick<AdminLocationSummary, "businessId" | "tenantId">>,
  tenantId: string | undefined,
) {
  if (!tenantId) return false;
  return (
    location.tenantId === tenantId ||
    location.businessId === tenantId ||
    location.restaurantId === tenantId
  );
}

export function assertUserCanResolveAdminLocation(
  user: AdminLocationUser,
  adminContext: AdminLocationContext,
  locations: AdminLocationSummary[] = PLATFORM_ADMIN_LOCATIONS,
) {
  if (
    user.ownerGroupId &&
    !locations.some(
      (location) => location.ownerGroupId === user.ownerGroupId,
    )
  ) {
    throw new Error("User is not assigned to a supported business group");
  }

  if (user.accessibleLocationIds !== undefined) {
    if (user.accessibleLocationIds.length === 0) {
      throw new Error("User has no business location access");
    }
    if (!user.accessibleLocationIds.includes(adminContext.locationId)) {
      throw new Error("User cannot access the active business location");
    }
  }
}

export function resolveAdminLocationContext(
  user: AdminLocationUser | null | undefined,
  locations: AdminLocationSummary[] = PLATFORM_ADMIN_LOCATIONS,
): AdminLocationContext {
  const ownerGroupId = getKnownOwnerGroupId(user?.ownerGroupId, locations);
  const ownerLocations = locations.filter(
    (location) => location.ownerGroupId === ownerGroupId,
  );
  const accessibleIds = new Set(user?.accessibleLocationIds ?? []);
  const accessibleLocations = ownerLocations.filter((location) =>
    accessibleIds.has(location.id),
  );
  const availableLocations =
    user?.accessibleLocationIds && accessibleLocations.length > 0
      ? accessibleLocations
      : ownerLocations;

  const defaultLocation = getDefaultLocation(locations);
  const requestedLocation = availableLocations.find(
    (location) => location.id === user?.defaultLocationId,
  );
  const requestedTenantId =
    user?.defaultTenantId ?? user?.defaultBusinessId ?? user?.defaultRestaurantId;
  const requestedTenantLocation =
    requestedTenantId && user?.defaultLocationId === undefined
      ? availableLocations.find(
          (location) => locationMatchesTenantId(location, requestedTenantId),
        )
      : undefined;
  const accessibleFallback =
    user?.accessibleLocationIds && accessibleLocations.length > 0
      ? accessibleLocations[0]
      : undefined;
  const activeLocation =
    requestedLocation ??
    requestedTenantLocation ??
    accessibleFallback ??
    availableLocations.find((location) => location.id === defaultLocation.id) ??
    availableLocations[0] ??
    defaultLocation;

  return {
    ownerGroupId: activeLocation.ownerGroupId,
    businessId: activeLocation.businessId,
    tenantId: activeLocation.tenantId,
    restaurantId: activeLocation.restaurantId,
    verticalId: activeLocation.verticalId,
    locationId: activeLocation.id,
    activeLocation,
    availableLocations,
  };
}

export function resolveAdminLocationContextForLocationId(
  user: AdminLocationUser | null | undefined,
  locations: AdminLocationSummary[] = PLATFORM_ADMIN_LOCATIONS,
  locationId: string | undefined,
): AdminLocationContext {
  const adminContext = resolveAdminLocationContext(user, locations);
  if (!locationId || adminContext.locationId === locationId) {
    return adminContext;
  }

  const targetLocation = adminContext.availableLocations.find(
    (location) =>
      location.id === locationId ||
      location.publicPath.split("/").filter(Boolean).at(-1) === locationId,
  );
  if (!targetLocation) {
    throw new Error("User cannot access the requested business location");
  }

  return {
    ...adminContext,
    ownerGroupId: targetLocation.ownerGroupId,
    businessId: targetLocation.businessId,
    tenantId: targetLocation.tenantId,
    restaurantId: targetLocation.restaurantId,
    verticalId: targetLocation.verticalId,
    locationId: targetLocation.id,
    activeLocation: targetLocation,
  };
}

export async function getAdminLocationRegistry(ctx: QueryCtx | MutationCtx) {
  const provisionedLocations = await ctx.db
    .query("provisioned_admin_locations")
    .collect();

  if (provisionedLocations.length === 0) {
    return PLATFORM_ADMIN_LOCATIONS;
  }

  const dynamicLocations = provisionedLocations.map(
    (location): AdminLocationSummary => ({
      id: location.locationId,
      businessId: location.businessId,
      tenantId: location.tenantId,
      restaurantId: location.restaurantId,
      verticalId: location.verticalId,
      ownerGroupId: location.ownerGroupId,
      name: location.name,
      shortName: location.shortName,
      publicPath: location.publicPath,
      publicUrl: location.publicUrl,
      capabilities: location.capabilities,
    }),
  );
  const locationIds = new Set(dynamicLocations.map((location) => location.id));

  return [
    ...PLATFORM_ADMIN_LOCATIONS.filter(
      (location) => !locationIds.has(location.id),
    ),
    ...dynamicLocations,
  ];
}

export async function resolveAdminLocationContextFromDb(
  ctx: QueryCtx | MutationCtx,
  user: AdminLocationUser | null | undefined,
) {
  return resolveAdminLocationContext(user, await getAdminLocationRegistry(ctx));
}

export function recordMatchesLocationContext(
  record: ScopedRestaurantRecord,
  adminContext: LocationContextLike,
): boolean {
  if (record.locationId) {
    return record.locationId === adminContext.locationId;
  }

  if (record.tenantId) {
    return (
      (adminContext.tenantId !== undefined &&
        record.tenantId === adminContext.tenantId) ||
      record.tenantId === adminContext.businessId ||
      record.tenantId === adminContext.restaurantId
    );
  }

  if (record.businessId) {
    return (
      record.businessId === adminContext.businessId ||
      (adminContext.tenantId !== undefined &&
        record.businessId === adminContext.tenantId) ||
      record.businessId === adminContext.restaurantId
    );
  }

  if (record.restaurantId) {
    return (
      record.restaurantId === adminContext.restaurantId ||
      record.restaurantId === adminContext.businessId ||
      (adminContext.tenantId !== undefined &&
        record.restaurantId === adminContext.tenantId)
    );
  }

  return adminContext.locationId === DEFAULT_LOCATION_ID;
}

export function filterRecordsForLocationContext<T extends ScopedRestaurantRecord>(
  records: T[],
  adminContext: LocationContextLike,
): T[] {
  return records.filter((record) =>
    recordMatchesLocationContext(record, adminContext),
  );
}

export function scopedInsertFieldsForLocationContext(
  adminContext: Pick<AdminLocationContext, "restaurantId" | "locationId">,
): ScopedInsertFields {
  return {
    restaurantId: adminContext.restaurantId,
    locationId: adminContext.locationId,
  };
}

export function businessScopedInsertFieldsForLocationContext(
  adminContext: LocationContextLike,
): BusinessScopedInsertFields {
  const businessId = adminContext.businessId ?? adminContext.restaurantId;
  const tenantId = adminContext.tenantId ?? businessId;

  return {
    businessId,
    tenantId,
    restaurantId: adminContext.restaurantId,
    locationId: adminContext.locationId,
  };
}

export function publicScopedInsertFieldsForLocationId(
  locationId: string | undefined,
): ScopedInsertFields {
  const location = getKnownPublicLocation(locationId);

  return {
    restaurantId: location.restaurantId,
    locationId: location.id,
  };
}

/**
 * Public scope for a location id that may live ONLY in the dynamic registry
 * (`provisioned_admin_locations`, written by the factory). The static
 * `PLATFORM_ADMIN_LOCATIONS` table is tried first so the hot path is
 * unchanged for shipped tenants; a factory-provisioned location resolves
 * through the same registry the admin side uses. Unknown ids still throw.
 */
export async function resolvePublicScopedInsertFieldsForLocationId(
  ctx: QueryCtx | MutationCtx,
  locationId: string | undefined,
): Promise<ScopedInsertFields> {
  if (locationId === undefined) {
    return publicScopedInsertFieldsForLocationId(undefined);
  }
  const staticLocation = PLATFORM_ADMIN_LOCATIONS.find(
    (candidate) => candidate.id === locationId,
  );
  if (staticLocation) {
    return { restaurantId: staticLocation.restaurantId, locationId: staticLocation.id };
  }
  const registry = await getAdminLocationRegistry(ctx);
  const location = registry.find((candidate) => candidate.id === locationId);
  if (!location) {
    throw new Error("Unsupported business location");
  }
  return { restaurantId: location.restaurantId, locationId: location.id };
}

export function publicBusinessScopedInsertFieldsForLocationId(
  locationId: string | undefined,
): BusinessScopedInsertFields {
  const location = getKnownPublicLocation(locationId);

  return {
    businessId: location.businessId,
    tenantId: location.tenantId,
    restaurantId: location.restaurantId,
    locationId: location.id,
  };
}

export function getReservationHoursForLocationId(
  locationId: string | undefined,
  dayOfWeek: number,
): ReservationHours {
  const { locationId: resolvedLocationId } =
    publicScopedInsertFieldsForLocationId(locationId);

  if (resolvedLocationId === ROMA_LOCATION_ID) {
    return ROMA_RESERVATION_HOURS_BY_DAY[dayOfWeek] ?? null;
  }

  return HOURS_BY_DAY[dayOfWeek] ?? null;
}

export function defaultScopedInsertFields(): ScopedInsertFields {
  return {
    restaurantId: DEFAULT_RESTAURANT_ID,
    locationId: DEFAULT_LOCATION_ID,
  };
}

export function defaultBusinessScopedInsertFields(): BusinessScopedInsertFields {
  const location = getDefaultLocation();

  return {
    businessId: location.businessId,
    tenantId: location.tenantId,
    restaurantId: location.restaurantId,
    locationId: location.id,
  };
}

// --- Support-ticket scoping: scope to ALL of an admin's accessible locations
// (not just the single active one) so an owner managing several restaurants sees
// a combined inbox, while cross-owner-group access is still blocked. ---

type AvailableBusinessLocationLike = Pick<
  AdminLocationSummary,
  "id" | "restaurantId"
> &
  Partial<Pick<AdminLocationSummary, "businessId" | "tenantId">>;

export function recordMatchesAnyAvailableLocation(
  record: ScopedRestaurantRecord,
  availableLocations: AvailableBusinessLocationLike[],
): boolean {
  if (record.locationId) {
    return availableLocations.some((l) => l.id === record.locationId);
  }
  if (record.tenantId) {
    return availableLocations.some((l) => locationMatchesTenantId(l, record.tenantId));
  }
  if (record.businessId) {
    return availableLocations.some((l) => locationMatchesTenantId(l, record.businessId));
  }
  if (record.restaurantId) {
    return availableLocations.some((l) =>
      locationMatchesTenantId(l, record.restaurantId),
    );
  }
  // Fully unscoped legacy rows belong to the default location.
  return availableLocations.some((l) => l.id === DEFAULT_LOCATION_ID);
}

export function filterRecordsForAvailableLocations<T extends ScopedRestaurantRecord>(
  records: T[],
  availableLocations: AvailableBusinessLocationLike[],
  narrowToLocationId?: string,
): T[] {
  const pool = narrowToLocationId
    ? availableLocations.filter((l) => l.id === narrowToLocationId)
    : availableLocations;
  return records.filter((record) => recordMatchesAnyAvailableLocation(record, pool));
}

export type TicketStatus = "open" | "pending" | "resolved" | "closed";
export type LegacyContactStatus = "new" | "read" | "replied" | "archived";

/** Map the legacy contacts.status onto the support ticketStatus for rows not yet migrated. */
export function deriveTicketStatus(legacy: LegacyContactStatus): TicketStatus {
  switch (legacy) {
    case "replied":
      return "pending";
    case "archived":
      return "closed";
    default:
      return "open"; // new | read
  }
}

/** Inverse of deriveTicketStatus: keep the legacy status field consistent on writes. */
export function legacyStatusForTicketStatus(status: TicketStatus): LegacyContactStatus {
  switch (status) {
    case "open":
      return "read";
    case "pending":
      return "replied";
    case "resolved":
    case "closed":
      return "archived";
  }
}
